import React, { useContext, useEffect, useRef, useState } from 'react';

import { DeviceType } from '@amzn/monera-app-core';
import { Alert, PageContext, ProductTwisterPlus, SubsBuyBox } from '@amzn/monera-app-core-components';
import { AlertProps } from '@amzn/monera-app-core-components/lib/components/alert/types';
import { isBrowser } from '@amzn/monera-app-core/lib/utils/env';

import { compareClickHandler, triggerPageUpdate } from './clientSideUtils';
import {
  BuyBoxContainer,
  carouselDimensions,
  DeviceSubsDesktopAlertWrapper,
  DeviceSubsMobileAlertWrapper,
  LeftDummyContainer,
  MobileBuyBoxContainer,
  ParentWrapperContainer
} from './styles';
import { TwisterBuyBoxProps } from './types';

const DEFAULT_MOBILE_ALERT_VALUES: Partial<AlertProps> = {
  maxWidth: '412px',
  alertType: 'information'
};

const DEFAULT_DESKTOP_ALERT_VALUES: Partial<AlertProps> = {
  maxWidth: '700px',
  alertType: 'information'
};

const useCarouselArrows = (dimensionValues?: string[][], rowIndexToDisplayVariationDetails?: number) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showArrows, setShowArrows] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current && dimensionValues?.length) {
        const { clientWidth } = containerRef.current;
        const buttonCount = dimensionValues[0]?.length || 0;

        if (buttonCount <= carouselDimensions.MIN_BUTTONS_FOR_ARROWS) {
          setShowArrows(false);
          return;
        }

        const buttonWidth =
          rowIndexToDisplayVariationDetails === 0
            ? carouselDimensions.BUTTON_MIN_WIDTH_WITH_DETAILS
            : carouselDimensions.BUTTON_MIN_WIDTH_DEFAULT;

        const buttonSpacing = carouselDimensions.BUTTON_MARGIN;
        const buyBoxPadding = carouselDimensions.BUYBOX_HORIZONTAL_PADDING;

        const availableWidth = clientWidth - buyBoxPadding;
        const requiredWidth = buttonCount * buttonWidth + (buttonCount - 1) * buttonSpacing;

        setShowArrows(requiredWidth > availableWidth);
      }
    };

    if (!isBrowser()) {
      return;
    }
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [dimensionValues, rowIndexToDisplayVariationDetails]);

  return { containerRef, showArrows };
};

const MobileBuyBox = (props: TwisterBuyBoxProps) => {
  if (props.buyBox.alert) {
    return (
      <DeviceSubsMobileAlertWrapper data-cy={'subs-mobile-buy-box-alert'}>
        <Alert {...DEFAULT_MOBILE_ALERT_VALUES} {...props.buyBox.alert} />
      </DeviceSubsMobileAlertWrapper>
    );
  }

  return (
    <MobileBuyBoxContainer id={props.id}>
      <ProductTwisterPlus
        {...props.twister}
        isLoading={props.isLoading}
        compareClickHandler={compareClickHandler}
        showMobileTopDivider={false}
      />
      <SubsBuyBox {...props.buyBox} showMobileTopDivider={false} showMobileBottomDivider={false} />
    </MobileBuyBoxContainer>
  );
};

/**
 * Buy box with twister that hides when buy box props are empty.
 * @param props
 * @constructor
 */
export const TwisterBuyBox = (props: TwisterBuyBoxProps) => {
  const triggerPageUpdateHookDeps = [props.buyBox && props.buyBox.callToAction && props.buyBox.callToAction.url];
  // Triggering page update to refresh the persistent navigation widget when ASIN or the checkout url changes.
  useEffect(() => {
    triggerPageUpdate();
  }, triggerPageUpdateHookDeps);

  const deviceType = useContext(PageContext).deviceType;
  if ((!props.buyBox || !Object.keys(props.buyBox).length) && (!props.twister || !Object.keys(props.twister).length)) {
    return null;
  }

  return deviceType === DeviceType.Mobile || deviceType === DeviceType.MShop ? (
    <MobileBuyBox {...props} />
  ) : (
    <DesktopBuyBox {...props} />
  );
};

const DesktopBuyBox = (props: TwisterBuyBoxProps) => {
  const { containerRef, showArrows } = useCarouselArrows(
    props.twister?.dimensionValues,
    props.twister?.rowIndexToDisplayVariationDetails
  );
  const { showPaginationButtons, ...twisterProps } = props.twister || {};

  if (props.buyBox.alert) {
    return (
      <DeviceSubsDesktopAlertWrapper data-cy={'subs-desktop-buy-box-alert'}>
        <Alert {...DEFAULT_DESKTOP_ALERT_VALUES} {...props.buyBox.alert} />
      </DeviceSubsDesktopAlertWrapper>
    );
  }

  return (
    <ParentWrapperContainer>
      <LeftDummyContainer />
      <BuyBoxContainer id={props.id} ref={containerRef}>
        {Object.keys(props.twister).length ? (
          <>
            <ProductTwisterPlus
              {...twisterProps}
              isLoading={props.isLoading}
              showPaginationButtons={showArrows}
              compareClickHandler={compareClickHandler}
            />
          </>
        ) : null}
        <SubsBuyBox {...props.buyBox} />
      </BuyBoxContainer>
    </ParentWrapperContainer>
  );
};
